import type { WrapStyle } from '@/lib/object-effects/types/wrap-style'
import { OFFSET_SIDES } from '../constants/wrap-offsets'
import type { OffsetKey, OffsetSide, WrapCarrier } from '../types/wrap-offset'

export function normWrapStyle(obj: WrapCarrier): WrapStyle {
  return obj.wrapStyle ?? 'none'
}

/** The `OFFSET_SIDES` entry (with its `field` name) for one side key. */
export function sideForKey(key: OffsetKey): OffsetSide {
  return OFFSET_SIDES.find((s) => s.key === key)!
}

/** Which offset keys a style treats as its own independent, rememberable data — used both to
 *  snapshot a style's current values into per-object offset memory before switching away from
 *  it, and to decide which fields to restore (from memory, or the 6pt default if this style has
 *  never been customized for this object) when switching to it. The backend has ONE shared set of
 *  four offset fields for the whole object, not one set per style, so Jump/Wrap Shape/Bounding Box
 *  editing the "same" wire field is what makes one style's edit silently overwrite another's
 *  remembered number unless this session-only memory intervenes. `'none'` is included with all
 *  four keys so landing on No Wrap always explicitly shows the 6pt default (never a leftover
 *  number from whatever style was active before it) without needing special-case logic elsewhere —
 *  its fields are disabled/uneditable anyway, so nothing meaningful is ever recorded FOR 'none',
 *  only snapshotted defensively when leaving it. */
export function relevantOffsetKeysForStyle(style: WrapStyle): readonly OffsetKey[] {
  if (style === 'wrap') return ['top']
  if (style === 'jump') return ['top', 'bottom']

  return ['top', 'right', 'bottom', 'left']
}

/** The `OFFSET_SIDES` entries a style treats as real, persistable data — same partition as
 *  `relevantOffsetKeysForStyle`, just pre-joined against `OFFSET_SIDES` for callers that need the
 *  `field` name too (the shared preset/slider/input control's commit + live-preview paths). Used
 *  PER TARGET now (each selected object may be on a different style), not against a single
 *  component-wide "current style" — that's the one behavioral change multi-selection required
 *  here; single selection still resolves to exactly the same list it always did. */
export function fieldsForStyle(style: WrapStyle): OffsetSide[] {
  const keys = relevantOffsetKeysForStyle(style)

  return OFFSET_SIDES.filter((s) => keys.includes(s.key))
}

/** Whether the shared preset/slider/input control is meaningful at all for a given style. */
export function offsetControlsEnabledForStyle(style: WrapStyle): boolean {
  return style === 'boundingBox' || style === 'wrap' || style === 'jump'
}

/** Whether one specific directional side is independently editable for a given style — the same
 *  partition `isSideOffsetDisabled` always encoded, extracted as a pure function of an explicit
 *  style so it can be evaluated per-target for multi-selection (each target may be on a different
 *  style) instead of only against a single shared `wrapStyle` variable. Wrap Shape has no
 *  individually-editable directional box at all — the shared preset/slider/input control above is
 *  its ONLY active offset editor (see `isOffsetDisabled`); all four boxes stay disabled,
 *  display-only mirrors of that one value (`offsetStringsFromObj`). */
export function sideEnabledForStyle(key: OffsetKey, style: WrapStyle): boolean {
  if (style === 'boundingBox') return true
  if (style === 'jump') return key === 'top' || key === 'bottom'

  return false
}
